import { addGeodata, cloneGeodata, replaceGeodata, type GeodataCloneMode } from 'api/geodataApi';
import type { GeodataState, GeodataParams } from 'contexts/GeodataContext';
import type { GeodataInsert } from '@urbanmetrix/sdk';

export type GeodataCloneModuleSelection = {
  enabled: boolean;
  mode: Extract<GeodataCloneMode, 'rerun' | 'clone'>;
};

export type GeodataCloneSelections = {
  terrain: GeodataCloneModuleSelection;
  buildings: GeodataCloneModuleSelection;
  trees: GeodataCloneModuleSelection;
};

const getImportedFileId = (value: unknown) =>
  value && typeof value === 'object' && 'id' in value ? String(value.id) : null;

export const buildGeodataBuildingImportUploads = (geodataParams: Record<string, unknown>) =>
  (
    [
      ['customObstruction', geodataParams.fileForCustomObstructions],
      ['projectSketches', geodataParams.fileForProjectSketches],
    ] as const
  ).flatMap(([kind, file]) => (typeof File !== 'undefined' && file instanceof File ? [{ kind, file }] : []));

const buildGeodataTerrainPayload = (geodataParams: Record<string, unknown>) => ({
  sizeDefinedBy: 'ANALYSIS AREA' as NonNullable<GeodataInsert['terrain']>['sizeDefinedBy'],
  lengthMid: Number(geodataParams?.lengthMid ?? 500),
  resolutionMid: Number(geodataParams?.resolutionMid ?? 5),
  lengthFar: Number(geodataParams?.lengthFar ?? 5000),
  resolutionFar: Number(geodataParams?.resolutionFar ?? 180),
});

const buildGeodataBuildingPayload = (geodataParams: Record<string, unknown>) => ({
  isInCloseTerrain: true,
  isInIntermediateTerrain: true,
  isBuildingInAnalysisArea: Boolean(geodataParams?.isBuildingInAnalysisArea),
  buildingsAsProjectSketches: Boolean(geodataParams?.buildingsAsProjectSketches),
  customObstructionS3ObjectId: getImportedFileId(geodataParams?.fileForCustomObstructions),
  projectSketchesS3ObjectId: getImportedFileId(geodataParams?.fileForProjectSketches),
});

const buildGeodataVegetationPayload = (geodataParams: Record<string, unknown>) => ({
  treesRadius: Number(geodataParams?.treesRadius ?? 200),
  treesMinimalHeight: Number(geodataParams?.treesMinimalHeight ?? 3),
  treesDonutRadius: Number(geodataParams?.treesDonutRadius ?? 1),
  chipperCapacity: Math.round(Number(geodataParams?.chipperCapacity ?? 7500)),
  minPoints: Math.round(Number(geodataParams?.minPoints ?? 100)),
  cecimationCellEdge: Number(geodataParams?.cecimationCellEdge ?? 0.75),
  concaveAlphaValue: geodataParams?.concaveAlphaValue == null ? null : Number(geodataParams?.concaveAlphaValue ?? 2),
  treesInAnalysisArea: Boolean(geodataParams?.treesInAnalysisArea),
});

export const buildGridGeodataPayload = (
  geodata: GeodataState,
  geodataParams: GeodataParams | Record<string, unknown>
) => ({
  name: geodata.name || 'Geodata 1',
  description: geodata.description || null,
  terrain: buildGeodataTerrainPayload(geodataParams as Record<string, unknown>),
  building: geodata.buildingsEnabled
    ? buildGeodataBuildingPayload(geodataParams as Record<string, unknown>)
    : undefined,
  vegetation: geodata.treesEnabled
    ? buildGeodataVegetationPayload(geodataParams as Record<string, unknown>)
    : undefined,
});

export const buildGridGeodataReplacePayload = (
  geodata: GeodataState,
  geodataParams: GeodataParams | Record<string, unknown>
) => {
  const payload = buildGridGeodataPayload(geodata, geodataParams);
  return { ...payload, building: payload.building ?? null, vegetation: payload.vegetation ?? null };
};

type PersistDraftGeodataArgs = {
  gridId: string;
  draftGeodataId?: string | null;
  geodata: GeodataState;
  geodataParams: GeodataParams | Record<string, unknown>;
  cloneSourceGeodataId?: string | null;
  cloneSelections?: GeodataCloneSelections | null;
};

const resolveCloneLayerMode = (
  selection: GeodataCloneModuleSelection | undefined,
  fallbackEnabled = false
): GeodataCloneMode =>
  !selection || !selection.enabled ? 'disabled' : (selection.mode ?? (fallbackEnabled ? 'rerun' : 'disabled'));

export const persistDraftGeodata = async ({
  gridId,
  draftGeodataId = null,
  geodata,
  geodataParams,
  cloneSourceGeodataId = null,
  cloneSelections = null,
}: PersistDraftGeodataArgs) => {
  if (cloneSourceGeodataId && cloneSelections) {
    return cloneGeodata(String(cloneSourceGeodataId), {
      gridId: String(gridId),
      name: geodata.name || 'Geodata 1',
      description: geodata.description || null,
      terrain: {
        mode: resolveCloneLayerMode(cloneSelections.terrain, true),
        ...(cloneSelections.terrain.mode === 'rerun'
          ? { input: buildGeodataTerrainPayload(geodataParams as Record<string, unknown>) }
          : {}),
      },
      building: {
        mode: resolveCloneLayerMode(cloneSelections.buildings, geodata.buildingsEnabled),
        ...(cloneSelections.buildings.enabled && cloneSelections.buildings.mode === 'rerun'
          ? { input: buildGeodataBuildingPayload(geodataParams as Record<string, unknown>) }
          : {}),
      },
      vegetation: {
        mode: resolveCloneLayerMode(cloneSelections.trees, geodata.treesEnabled),
        ...(cloneSelections.trees.enabled && cloneSelections.trees.mode === 'rerun'
          ? { input: buildGeodataVegetationPayload(geodataParams as Record<string, unknown>) }
          : {}),
      },
    });
  }

  const payload = {
    gridId: String(gridId),
    ...buildGridGeodataPayload(geodata, geodataParams),
  };

  if (draftGeodataId) {
    return replaceGeodata(String(draftGeodataId), payload as never);
  }

  return addGeodata(payload as never);
};
